import {InputSelect} from '@/components/commons/input-select/InputSelect'
import {InputTime} from '@/components/commons/input-time/InputTime'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {AlertCircleIcon} from '@/components/ui/icon/Icon'
import {Label} from '@/components/ui/label/Label'
import {formatLocaleDate} from '@/dayjs'
import {StyledReadonlyDate} from '@/features/agenda/components/agenda-event-form/agenda-event-date-time-selector/style'
import {StyledAgendaDateRoomFields} from '@/features/agenda/components/agenda-event-form/agenda-event-form-fields/style'
import {AgendaEventFieldsSchema} from '@/features/agenda/formSchemas'
import {agendaDurationMapToLabel, locationMapToLabel} from '@/features/agenda/utils'
import {useTranslation} from '@/translations/i18n'
import dayjs from 'dayjs'
import {useState} from 'react'
import {Controller, useFormContext, useWatch} from 'react-hook-form'
import {z} from 'zod'
import {StyledAlertSchedulesChanges} from '@/features/agenda/components/agenda-event-form/agenda-event-repeat-schedule-fields/style'
import {ObjectEntries, ObjectKeys} from '@/utilities/helpers'
import {Text} from '@/components/ui/text/Text'
import {InputDate} from '@/components/commons/input-date/InputDate'
import {useGetAvailabilities} from '@/features/agenda/queries/useGetUsersAvailabilities'
import {isValidDate} from '@/components/commons/input-date/utils'
import {useGetEvents} from '@/features/agenda/queries/useGetEvents'
import {AlertMessage} from '@/components/commons/alert-message/AlertMessage'
import {useAvailabilityWarnings} from './useAvailabilityWarnings'
import {useDisabledSlots} from './useDisabledSlots'

const AgendaEventDateTimeSelectorSchema = AgendaEventFieldsSchema.pick({
    consultants: true,
    patients: true,
    date: true,
    eventTeamMembers: true,
    duration: true
})
type AgendaEventDateTimeSelectorSchema = z.infer<typeof AgendaEventDateTimeSelectorSchema>

export const AgendaEventDateTimeSelector = ({
    index,
    mode,
    readOnlyDates,
    defaultDate
}: {
    index: number
    mode?: 'create' | 'edit'
    readOnlyDates?: boolean
    defaultDate?: Date
}) => {
    const {t} = useTranslation()
    const {control, setValue, clearErrors} = useFormContext<AgendaEventDateTimeSelectorSchema>()

    // Form values
    const targetDate = useWatch({control, name: `date.date`})
    const duration = useWatch({control, name: 'duration'})
    const consultantId = useWatch({control, name: `consultants.${0}.id`})
    const patients = useWatch({control, name: `patients`})
    const teamMembers = useWatch({control, name: `eventTeamMembers`})
    const accessType = useWatch({control, name: `consultants.${0}.accessType.id`})
    const selectedTime = useWatch({control, name: `date.time`})
    const locationValue = useWatch({control, name: `date.location`})

    // Month displayed in the calendar
    const [month, setMonth] = useState<Date>(targetDate ?? new Date())

    // Get availabilities
    const {data: availabilities} = useGetAvailabilities(
        {
            therapists: consultantId
                ? teamMembers
                    ? teamMembers.map(tm => tm.user.id).concat(consultantId)
                    : [consultantId]
                : [],
            startDate: dayjs(month).startOf('month').format('YYYY-MM-DD'),
            endDate: dayjs(month).endOf('month').format('YYYY-MM-DD')
        },
        {
            enabled: !!consultantId && !!duration
        }
    )

    // Get patients events
    const {data: patientsEvents} = useGetEvents({
        params: {
            startDate: dayjs(month).startOf('month').format('YYYY-MM-DD'),
            endDate: dayjs(month).endOf('month').format('YYYY-MM-DD'),
            therapists: [],
            patients: patients ? patients.map(p => p.id) : []
        },
        options: {
            enabled: !!patients && patients.length > 0
        }
    })

    // Calculate all disabled slots and dates
    const {
        consultantUnavailableDates,
        patientUnavailableDates,
        consultantDisabledSlots,
        patientDisabledSlots,
        includedSlots,
        disabledDates,
        allMonthDisabled,
        warningDates
    } = useDisabledSlots({
        availabilities,
        patientsEvents: patientsEvents || [],
        targetDate,
        targetMonth: month,
        duration,
        patients
    })

    // Availability warnings
    const availabilitiesWarnings = useAvailabilityWarnings({
        targetDate,
        selectedTime,
        consultantUnavailableDates,
        patientUnavailableDates,
        consultantDisabledSlots,
        patientDisabledSlots,
        patients,
        allMonthDisabled,
        skipDatesCheck: defaultDate ? [defaultDate] : undefined,
        duration: duration ? parseInt(duration) : undefined
    })

    const handleChangeDurationSideEffects = () => {
        setValue(`date.date`, new Date(''))
        setValue(`date.time`, '')
    }

    const handleChangeDateSideEffects = (value: Date | null) => {
        if (value) {
            clearErrors(`date`)
        }

        setValue(`date.time`, '')
    }

    return (
        <Flexbox direction="column" gap={4}>
            <StyledAgendaDateRoomFields>
                {index == 0 && mode == 'edit' ? (
                    <Controller
                        control={control}
                        name="duration"
                        render={({field, fieldState}) => {
                            return (
                                <InputSelect
                                    label={t('commons:duration')}
                                    value={field.value ? field.value : undefined}
                                    errorMessage={fieldState.error?.message}
                                    onChange={value => {
                                        field.onChange(value)
                                        handleChangeDurationSideEffects()
                                    }}
                                    disabled={!accessType}
                                    optionsEntries={ObjectEntries(agendaDurationMapToLabel).map(([key, value]) => [
                                        key.toString(),
                                        t(value, {value: key}).toString()
                                    ])}
                                />
                            )
                        }}
                    />
                ) : (
                    <Flexbox direction="column" gap={1.5}>
                        <Label>{t('commons:duration')}</Label>
                        <Text palette="neutral" shade="600" size="textSm">
                            {duration ? t('commons:duration_minutes', {value: duration}) : '-'}
                        </Text>
                    </Flexbox>
                )}

                {readOnlyDates ? (
                    <Flexbox direction="column" gap={1.5}>
                        <Label>{t('commons:date')}</Label>
                        <StyledReadonlyDate>{formatLocaleDate(targetDate, 'dddd DD MMMM')}</StyledReadonlyDate>
                    </Flexbox>
                ) : (
                    <Controller
                        control={control}
                        name={`date.date`}
                        render={({field, fieldState}) => (
                            <InputDate
                                label={t('commons:date')}
                                placeholder="GG/MM/AAAA"
                                errorMessage={fieldState.error?.message ? t('errors:required') : undefined}
                                fullWidth
                                disabled={!duration || allMonthDisabled}
                                value={
                                    targetDate && isValidDate(dayjs(targetDate).format('YYYY-MM-DD'), 'YYYY-MM-DD')
                                        ? field.value
                                        : null
                                }
                                disabledDates={disabledDates}
                                warningDates={warningDates}
                                onMonthChange={month => setMonth(month)}
                                highlightAvailabilities
                                onChange={value => {
                                    field.onChange(value)
                                    handleChangeDateSideEffects(value)
                                }}
                            />
                        )}
                    />
                )}

                <Flexbox direction="column" gap={1.5} fullWidth>
                    <Controller
                        control={control}
                        name={`date.time`}
                        render={({field, fieldState}) => (
                            <InputTime
                                label={t('agenda:start_time')}
                                value={targetDate ? field.value : ''}
                                errorMessage={fieldState.error?.message}
                                startTime="08:00"
                                endTime="18:45"
                                interval={15}
                                // availableSlots={includedSlots}
                                warningSlots={includedSlots}
                                fullWidth
                                disabled={!targetDate}
                                placeholder={
                                    (availabilities && ObjectKeys(availabilities).length > 0) || availabilities !== null
                                        ? t('agenda:select_time')
                                        : '-'
                                }
                                onChange={field.onChange}
                            />
                        )}
                    />

                    {availabilities && ObjectKeys(availabilities).length == 0 && (
                        <StyledAlertSchedulesChanges gap={1.5} align="center">
                            <AlertCircleIcon />
                            <p>{t('agenda:no_available_slot')}</p>
                        </StyledAlertSchedulesChanges>
                    )}
                </Flexbox>

                <Controller
                    control={control}
                    name={`date.location`}
                    render={({field, fieldState}) => (
                        <InputSelect
                            label={t('agenda:room')}
                            value={locationValue}
                            placeholder={t('agenda:select_room')}
                            optionsEntries={ObjectEntries(locationMapToLabel)
                                .filter(([key]) => key != 'gym')
                                .map(([key, value]) => [key, t(value)])}
                            errorMessage={fieldState.error?.message}
                            onChange={field.onChange}
                            disabled={!targetDate || !consultantId}
                        />
                    )}
                />
            </StyledAgendaDateRoomFields>
            {availabilitiesWarnings.length > 0 && (
                <Flexbox direction="column" gap={1.5}>
                    {availabilitiesWarnings.map((warning, index) => (
                        <AlertMessage
                            key={index}
                            message={warning}
                            icon={<AlertCircleIcon />}
                            palette="orange"
                            shade="800"
                            size="textSm"
                            iconSize={14}
                        />
                    ))}
                </Flexbox>
            )}
        </Flexbox>
    )
}
